// touch-controls.js — mobile/tablet input layer that mirrors the public API
// of controls.js. Picks itself instead of PointerLockControls when main.js
// detects a touch device.
//
// Layout:
//   • Left half of screen → floating joystick. First touch in the left half
//     spawns the visible joystick at that point; the thumb follows finger
//     deflection, output is a [-1..1] vector consumed in update().
//   • Right half of screen → drag-to-look. yaw rotates around world Y, pitch
//     rotates around the camera's local X (clamped near ±90° so you can't
//     flip upside down).
//   • Tap (any short, ~stationary touch on the canvas) → fires onTap()
//     listeners. main.js wires that to openInfoPanel(currentSelection).
//
// We use Pointer Events (pointerdown / pointermove / pointerup), which on
// modern browsers cover touch, mouse, and stylus uniformly. Multi-touch is
// supported by tracking each pointerId separately.
//
// Camera hierarchy on touch is yaw → pitch → camera. PointerLockControls
// rotates the camera directly via Euler — we instead manage rotation through
// two Object3D wrappers, which keeps yaw-only horizontal movement trivial.

import * as THREE from 'three';
import { ROOM } from './scene.js';

// ---------- Collision constants (mirrored from controls.js) ----------
// Duplicated rather than shared since controls.js doesn't export them and
// they're tiny + stable. If we ever change room geometry we'll factor these
// into a shared collision module.
const PAD = 0.55;
const X_MIN = -ROOM.W / 2 + 1.05 + PAD;
const X_MAX =  ROOM.W / 2 - 1.05 - PAD;
const Z_MIN = -ROOM.D / 2 + 1.05 + PAD;
const Z_MAX =  ROOM.D / 2 - PAD;
const EYE_HEIGHT = 1.65;
const SPEED = 3.4;
const OBSTACLES = [
  { x:  6.5, z:  4.5, radius: 0.95 },   // chocolate fountain
  { x: -6.5, z: -3.5, radius: 1.10 },   // gummy mountain
];
const RECT_OBSTACLES = [
  { minX: -0.6, maxX: 3.6, minZ: -5.95, maxZ: -5.05, pad: 0.40 },   // counter
];

// ---------- Touch input tuning ----------
const JOY_RADIUS = 50;          // px — max thumb deflection from center
const JOY_VISUAL_RADIUS = 60;   // px — half of #joystick element size (120px)
const JOY_DEADZONE = 0.15;      // fractional, ignore tiny stick wobble
const SENSITIVITY = 0.005;      // rad/px during drag-to-look
const TAP_MAX_MS = 300;
const TAP_MAX_PX = 10;
const PITCH_LIMIT = Math.PI / 2 - 0.1;

export function createTouchControls(camera, canvas) {
  // yaw + pitch wrappers. yaw owns world position + horizontal rotation;
  // pitch (its child) owns vertical rotation. Camera sits inside pitch.
  const yaw = new THREE.Object3D();
  const pitch = new THREE.Object3D();
  yaw.add(pitch);
  pitch.add(camera);

  // The camera was positioned by main.js at (0, EYE_HEIGHT, 6) before this
  // module was constructed. Move that position onto the yaw wrapper and
  // zero the camera's local transform so the wrappers fully drive it.
  yaw.position.copy(camera.position);
  if (yaw.position.lengthSq() === 0) yaw.position.set(0, EYE_HEIGHT, 6);
  yaw.position.y = EYE_HEIGHT;
  camera.position.set(0, 0, 0);
  camera.rotation.set(0, 0, 0);

  let pitchAngle = 0;
  const moveVec = new THREE.Vector2(0, 0);

  // Per-pointer state. Multi-touch: a left-half pointer (joystick) and a
  // right-half pointer (look) can be active simultaneously.
  // pointerId → { kind, startX, startY, lastX, lastY, t0, isTap }
  const pointers = new Map();
  const tapListeners = new Set();

  // Joystick visual elements live in index.html — we just toggle visibility
  // and apply transforms.
  const joystickEl = document.getElementById('joystick');
  const joystickThumbEl = document.getElementById('joystick-thumb');

  function leftHalf(x) { return x < window.innerWidth / 2; }
  function findKind(kind) {
    for (const p of pointers.values()) if (p.kind === kind) return p;
    return null;
  }

  function onPointerDown(e) {
    // Only respond to pointers that originated on the canvas. UI buttons
    // (cart counter, info panel, portrait hint) live above the canvas in z
    // order and have their own click handlers — we mustn't swallow them.
    if (e.target !== canvas) return;
    // Ignore actual mouse pointer-downs so a desktop browser that's also
    // running this module via touch detection (rare hybrid) doesn't double-
    // up with the desktop controls. Touch + pen are the two we care about.
    if (e.pointerType === 'mouse') return;

    // setPointerCapture keeps pointermove events flowing even if the finger
    // wanders off-canvas. It throws NotFoundError for synthetic pointers
    // (non-OS-generated, e.g. test events), and there's a vanishingly small
    // browser race where it can throw on rapid taps. Either way it's not
    // load-bearing — pointer events still flow to the canvas in normal use.
    try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* no-op */ }
    const x = e.clientX, y = e.clientY;
    const isLeft = leftHalf(x);

    let kind = null;
    if (isLeft && !findKind('move')) {
      kind = 'move';
      // Position the joystick visual centered on the touch point.
      joystickEl.style.left = (x - JOY_VISUAL_RADIUS) + 'px';
      joystickEl.style.top  = (y - JOY_VISUAL_RADIUS) + 'px';
      joystickEl.hidden = false;
      joystickThumbEl.style.transform = 'translate(0px, 0px)';
    } else if (!isLeft && !findKind('look')) {
      kind = 'look';
    } else {
      return;     // already tracking a pointer of this kind
    }

    pointers.set(e.pointerId, {
      kind,
      startX: x, startY: y,
      lastX: x,  lastY: y,
      t0: performance.now(),
      isTap: true,
    });
  }

  function onPointerMove(e) {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    const x = e.clientX, y = e.clientY;
    const dx = x - p.startX, dy = y - p.startY;
    if (dx * dx + dy * dy > TAP_MAX_PX * TAP_MAX_PX) p.isTap = false;

    if (p.kind === 'move') {
      const len = Math.sqrt(dx * dx + dy * dy);
      const clamped = Math.min(len, JOY_RADIUS);
      const nx = len > 0 ? (dx / len) * (clamped / JOY_RADIUS) : 0;
      const ny = len > 0 ? (dy / len) * (clamped / JOY_RADIUS) : 0;
      const mag = Math.sqrt(nx * nx + ny * ny);
      if (mag < JOY_DEADZONE) moveVec.set(0, 0);
      else                    moveVec.set(nx, ny);
      // Visual: move the thumb proportionally, capped at JOY_RADIUS px.
      joystickThumbEl.style.transform = `translate(${nx * JOY_RADIUS}px, ${ny * JOY_RADIUS}px)`;
    } else if (p.kind === 'look') {
      const ddx = x - p.lastX;
      const ddy = y - p.lastY;
      yaw.rotation.y -= ddx * SENSITIVITY;
      pitchAngle = THREE.MathUtils.clamp(pitchAngle - ddy * SENSITIVITY, -PITCH_LIMIT, PITCH_LIMIT);
      pitch.rotation.x = pitchAngle;
    }
    p.lastX = x; p.lastY = y;
  }

  function onPointerEnd(e) {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    pointers.delete(e.pointerId);

    if (p.kind === 'move') {
      moveVec.set(0, 0);
      joystickEl.hidden = true;
    }

    // Tap detection: still flagged isTap (no significant drag) AND released
    // within TAP_MAX_MS. Fires registered onTap listeners with the tap's
    // client coordinates so callers can do screen-space hit-testing
    // (e.g. raycasting a product where the finger landed instead of
    // assuming the screen center).
    const elapsed = performance.now() - p.t0;
    if (p.isTap && elapsed < TAP_MAX_MS) {
      const tap = { x: p.startX, y: p.startY };
      for (const fn of tapListeners) {
        try { fn(tap); } catch (err) { console.error('[touch] tap listener:', err); }
      }
    }
  }

  // Movement primitives: yaw-only direction (looking up/down doesn't push you
  // into the floor). Mirrors PointerLockControls.moveForward/moveRight.
  const _forward = new THREE.Vector3();
  const _right = new THREE.Vector3();
  function moveForward(dist) {
    _forward.set(-Math.sin(yaw.rotation.y), 0, -Math.cos(yaw.rotation.y));
    yaw.position.addScaledVector(_forward, dist);
  }
  function moveRight(dist) {
    _right.set(Math.cos(yaw.rotation.y), 0, -Math.sin(yaw.rotation.y));
    yaw.position.addScaledVector(_right, dist);
  }

  function applyCollisions() {
    yaw.position.x = THREE.MathUtils.clamp(yaw.position.x, X_MIN, X_MAX);
    yaw.position.z = THREE.MathUtils.clamp(yaw.position.z, Z_MIN, Z_MAX);

    for (const o of OBSTACLES) {
      const dx = yaw.position.x - o.x;
      const dz = yaw.position.z - o.z;
      const d2 = dx * dx + dz * dz;
      const r2 = o.radius * o.radius;
      if (d2 < r2 && d2 > 0) {
        const d = Math.sqrt(d2);
        const push = (o.radius - d) / d;
        yaw.position.x += dx * push;
        yaw.position.z += dz * push;
      }
    }

    for (const r of RECT_OBSTACLES) {
      const minX = r.minX - r.pad, maxX = r.maxX + r.pad;
      const minZ = r.minZ - r.pad, maxZ = r.maxZ + r.pad;
      if (yaw.position.x <= minX || yaw.position.x >= maxX) continue;
      if (yaw.position.z <= minZ || yaw.position.z >= maxZ) continue;
      const dL = yaw.position.x - minX;
      const dR = maxX - yaw.position.x;
      const dB = yaw.position.z - minZ;
      const dF = maxZ - yaw.position.z;
      const m = Math.min(dL, dR, dB, dF);
      if (m === dF)      yaw.position.z = maxZ;
      else if (m === dB) yaw.position.z = minZ;
      else if (m === dL) yaw.position.x = minX;
      else               yaw.position.x = maxX;
    }

    yaw.position.y = EYE_HEIGHT;
  }

  function update(dt) {
    if (moveVec.lengthSq() > 0) {
      const step = SPEED * dt;
      // Joystick UP (negative screen Y) = forward in scene.
      moveForward(-moveVec.y * step);
      moveRight(moveVec.x * step);
      applyCollisions();
    }
  }

  canvas.addEventListener('pointerdown',   onPointerDown);
  canvas.addEventListener('pointermove',   onPointerMove);
  canvas.addEventListener('pointerup',     onPointerEnd);
  canvas.addEventListener('pointercancel', onPointerEnd);

  return {
    object: yaw,
    update,
    isLocked: () => true,         // No lock concept on touch — selection
                                  // prompt + interactions key off this.
    lockPointer:   () => {},      // no-op
    unlockPointer: () => {},      // no-op
    onTap: (fn) => { tapListeners.add(fn); return () => tapListeners.delete(fn); },
  };
}
